type InstallPromptChoice = {
  outcome: "accepted" | "dismissed";
  platform?: string;
};

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<InstallPromptChoice | void>;
  userChoice?: Promise<InstallPromptChoice>;
};

let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;

function isStandalone() {
  const iosNavigator = navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia("(display-mode: standalone)").matches || iosNavigator.standalone === true
  );
}

function wasInstalled() {
  return window.localStorage.getItem("100-codigos:installed") === "1";
}

function removeInstallBanner() {
  document.getElementById("install-app-banner")?.remove();
}

function hasBlockingDialog() {
  return Boolean(document.querySelector('[role="dialog"][aria-modal="true"]'));
}

function isAndroid() {
  return /Android/i.test(navigator.userAgent);
}

function isSamsungInternet() {
  return /SamsungBrowser/i.test(navigator.userAgent);
}

function isChromeAndroid() {
  return isAndroid() && /Chrome|CriOS/i.test(navigator.userAgent) && !isSamsungInternet();
}

function isLikelyInAppBrowser() {
  return /; wv\)|\bwv\b|FBAN|FBAV|Instagram|WhatsApp/i.test(navigator.userAgent);
}

function chromeIntentUrl() {
  const target = `${window.location.host}${window.location.pathname}${window.location.search}${window.location.hash}`;
  return `intent://${target}#Intent;scheme=https;package=com.android.chrome;end`;
}

function installHelpCopy() {
  if (isSamsungInternet()) {
    return {
      title: "Instale pelo Samsung Internet",
      body: "Toque no ícone de instalação na barra do navegador. Se ele não aparecer, abra o menu do Samsung Internet e escolha a opção de adicionar/instalar na tela de aplicativos.",
      showChromeButton: false,
    };
  }

  if (isChromeAndroid()) {
    return {
      title: "Instale pelo Chrome",
      body: "Abra o menu ⋮ do Chrome e toque em “Instalar app” ou “Adicionar à tela inicial”. Depois confirme a instalação.",
      showChromeButton: false,
    };
  }

  if (isAndroid()) {
    return {
      title: "Abra no Chrome para instalar",
      body: isLikelyInAppBrowser()
        ? "Este navegador interno não conseguiu abrir a instalação. Abra a página no Chrome e toque novamente em instalar."
        : "Seu navegador não abriu a instalação automaticamente. Abra esta página no Chrome ou Samsung Internet para instalar como aplicativo.",
      showChromeButton: true,
    };
  }

  return {
    title: "Instale pelo navegador",
    body: "Abra o menu de compartilhamento ou opções do navegador e escolha “Adicionar à Tela de Início” ou “Instalar app”.",
    showChromeButton: false,
  };
}

function showInstallHelp(banner: HTMLElement, appLabel: string) {
  const help = installHelpCopy();
  banner.setAttribute("aria-label", `Como instalar ${appLabel} no celular`);
  banner.innerHTML = `
    <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:12px">
      <div style="min-width:0;flex:1">
        <div style="font-weight:800;font-size:16px;line-height:1.3;color:#5b2344">${help.title}</div>
        <div style="margin-top:7px;font-size:13.5px;line-height:1.5;color:#62575e">${help.body}</div>
      </div>
      <button type="button" data-install-help-close aria-label="Fechar" style="border:0;background:transparent;color:#765968;font-size:22px;line-height:1;padding:4px;cursor:pointer">×</button>
    </div>
    ${
      help.showChromeButton
        ? `<a data-open-chrome href="${chromeIntentUrl()}" style="margin-top:13px;display:flex;min-height:48px;width:100%;align-items:center;justify-content:center;border-radius:999px;background:#7a315a;color:#fff;text-decoration:none;font-weight:800;font-size:13px;letter-spacing:.02em">ABRIR NO CHROME</a>`
        : ""
    }
    <button type="button" data-install-help-done style="margin-top:10px;width:100%;min-height:44px;border:1px solid rgba(91,35,68,.20);border-radius:999px;background:#fff;color:#5b2344;font-weight:800;font-size:13px;cursor:pointer">ENTENDI</button>
  `;

  const close = () => removeInstallBanner();
  banner.querySelector<HTMLElement>("[data-install-help-close]")?.addEventListener("click", close);
  banner.querySelector<HTMLElement>("[data-install-help-done]")?.addEventListener("click", close);
}

function createInstallBanner() {
  if (document.getElementById("install-app-banner") || isStandalone() || wasInstalled()) return;
  if (window.location.pathname !== "/") return;

  const isInannaHost =
    window.location.hostname === "codigodeinanna.dimmedia.net" ||
    window.location.hostname === "retomarpaz.dimmedia.net";
  const appLabel = isInannaHost ? "O Código de Inanna" : "100 Códigos";
  const appIcon = isInannaHost ? "/inanna-representacao-v1.webp" : "/icon-192.png";

  const banner = document.createElement("div");
  banner.id = "install-app-banner";
  banner.setAttribute("role", "dialog");
  banner.setAttribute("aria-label", `Instalar ${appLabel} no celular`);
  banner.style.cssText = [
    "position:fixed",
    "left:14px",
    "right:14px",
    "bottom:calc(14px + env(safe-area-inset-bottom))",
    "z-index:9999",
    "max-width:430px",
    "margin:0 auto",
    "background:#fffaf7",
    "border:1px solid rgba(92,31,59,.16)",
    "border-radius:22px",
    "box-shadow:0 18px 50px rgba(92,31,59,.24)",
    "padding:14px",
    "font-family:Montserrat,system-ui,sans-serif",
    "color:#4b2a37",
  ].join(";");

  banner.innerHTML = `
    <div style="display:flex;gap:12px;align-items:center">
      <img src="${appIcon}" alt="" style="width:52px;height:52px;border-radius:14px;object-fit:cover;flex:0 0 auto" />
      <div style="min-width:0;flex:1">
        <div style="font-weight:800;font-size:15px;line-height:1.25;color:#5c1f3b">Tenha ${appLabel} no seu celular</div>
        <div style="margin-top:3px;font-size:12.5px;line-height:1.4;color:rgba(75,42,55,.72)">Instale como um aplicativo e abra direto pela tela inicial.</div>
      </div>
      <button type="button" data-install-close aria-label="Fechar" style="border:0;background:transparent;color:#7e6370;font-size:22px;line-height:1;padding:4px;cursor:pointer">×</button>
    </div>
    <button type="button" data-install-action style="margin-top:12px;width:100%;min-height:46px;border:0;border-radius:999px;background:#7d234b;color:white;font-weight:800;font-size:13px;letter-spacing:.02em;cursor:pointer;touch-action:manipulation">INSTALAR NO CELULAR</button>
  `;

  banner.querySelector<HTMLElement>("[data-install-close]")?.addEventListener("click", () => {
    removeInstallBanner();
  });

  banner.querySelector<HTMLButtonElement>("[data-install-action]")?.addEventListener("click", async (clickEvent) => {
    clickEvent.preventDefault();
    clickEvent.stopPropagation();

    const action = clickEvent.currentTarget as HTMLButtonElement;
    const promptEvent = deferredInstallPrompt;

    if (!promptEvent) {
      showInstallHelp(banner, appLabel);
      return;
    }

    action.disabled = true;
    action.textContent = "ABRINDO INSTALAÇÃO...";

    try {
      const result = await promptEvent.prompt();
      deferredInstallPrompt = null;

      let choice = result;
      if (!choice && promptEvent.userChoice) {
        try {
          choice = await promptEvent.userChoice;
        } catch {
          choice = undefined;
        }
      }

      if (choice?.outcome === "accepted") {
        removeInstallBanner();
        return;
      }

      showInstallHelp(banner, appLabel);
    } catch (error) {
      console.warn("[Install] Não foi possível abrir o convite nativo de instalação.", error);
      deferredInstallPrompt = null;
      showInstallHelp(banner, appLabel);
    }
  });

  document.body.appendChild(banner);
}

function scheduleInstallBanner() {
  let attempts = 0;

  const tryShow = () => {
    attempts += 1;
    if (isStandalone() || wasInstalled() || window.location.pathname !== "/") return;
    if (document.getElementById("install-app-banner")) return;

    if (hasBlockingDialog()) {
      if (attempts < 30) window.setTimeout(tryShow, 1000);
      return;
    }

    createInstallBanner();
  };

  window.setTimeout(tryShow, 1400);
}

export function setupInstallAppPrompt() {
  if (typeof window === "undefined" || isStandalone() || wasInstalled()) return;

  window.addEventListener("beforeinstallprompt", (rawEvent) => {
    rawEvent.preventDefault();
    deferredInstallPrompt = rawEvent as BeforeInstallPromptEvent;
  });

  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    localStorage.setItem("100-codigos:installed", "1");
    removeInstallBanner();
  });

  scheduleInstallBanner();
}
